/**
 * NearbySection — 인근 단지 (v2 목업 상세 04 하단: 모바일 가로 캐러셀 / PC 4열).
 *
 * 데이터: 지도 경량 API(getMapData, 세션 캐시) 에서 좌표 거리로 가까운 단지 4곳(5km 이내, 자기 자신 제외).
 * 백엔드 변경 없음. 내부 링크(상세)만 — 외부 링크 없음. 배지는 buildBadges(상태는 '분양예정'만).
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { propertyApi, Property } from '../../services/api';
import { getPropertyUrl } from '../../utils/formatters';
import { formatUnits } from '../../domain/property/formatters';
import { buildBadges } from '../../domain/property/badges';
import { Badge } from '../../design-system/components/Badge';
import { SectionCard } from '../../design-system/components/section/SectionCard';
import { CompareIcon } from '../../design-system/components/section/SectionIcons';
import { buildImg, buildLoc } from '../map/mapHelpers';

const RADIUS_KM = 5;
const LIMIT = 4;

export function distanceKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6371;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

export function pickNearby(list: Property[], self: Property, limit = LIMIT, radiusKm = RADIUS_KM): Property[] {
  if (typeof self.latitude !== 'number' || typeof self.longitude !== 'number') return [];
  return list
    .filter((p) => p.id !== self.id && typeof p.latitude === 'number' && typeof p.longitude === 'number')
    .map((p) => ({ p, d: distanceKm(self.latitude as number, self.longitude as number, p.latitude as number, p.longitude as number) }))
    .filter((x) => x.d <= radiusKm)
    .sort((a, b) => a.d - b.d)
    .slice(0, limit)
    .map((x) => x.p);
}

export const NearbySection: React.FC<{ property: Property }> = ({ property }) => {
  const [items, setItems] = useState<Property[]>([]);

  useEffect(() => {
    let alive = true;
    propertyApi
      .getMapData()
      .then((list) => {
        if (alive) setItems(pickNearby(list, property));
      })
      .catch(() => {
        if (alive) setItems([]);
      });
    return () => {
      alive = false;
    };
  }, [property]);

  if (items.length === 0) return null;

  return (
    <SectionCard title="인근 단지" icon={<CompareIcon />}>
      <div
        className="-mx-4 flex gap-2.5 overflow-x-auto px-4 pb-1 lg:mx-0 lg:grid lg:grid-cols-4 lg:overflow-visible lg:px-0"
        style={{ scrollbarWidth: 'none' }}
        data-testid="nearby-list"
      >
        {items.map((p) => {
          const img = buildImg(p);
          const badges = buildBadges(p).slice(0, 1);
          return (
            <Link
              key={p.id}
              to={getPropertyUrl(p)}
              className="flex w-[200px] shrink-0 flex-col gap-2 rounded-ca-2xl bg-frame p-2.5 shadow-ca-card lg:w-auto"
            >
              <div className="h-[84px] w-full overflow-hidden rounded-ca-lg bg-ca-surface">
                {img && <img src={img} alt={p.title} loading="lazy" className="h-full w-full object-cover" />}
              </div>
              {badges.length > 0 && (
                <span className="self-start">
                  <Badge tone={badges[0].tone} size="tag" ariaLabel={badges[0].ariaLabel}>
                    {badges[0].label}
                  </Badge>
                </span>
              )}
              <b className="line-clamp-1 text-[14px] font-bold text-ink">{p.title}</b>
              <span className="line-clamp-1 text-[11px] font-bold text-mute2">
                {buildLoc(p)}
                {p.total_units && p.total_units > 0 ? ` · ${formatUnits(p, { short: true })}` : ''}
              </span>
            </Link>
          );
        })}
      </div>
    </SectionCard>
  );
};

export default NearbySection;
